import { type CountryCode, normalizePhoneToE164 } from '@onefootprint/core';
import { getLogger } from './logger';

const { logWarn } = getLogger({ name: 'idv2', location: 'filter-bootstrap-data' });

export type FilterBootstrapDataResult<T extends Record<string, unknown>> = {
  /** Bootstrap data filtered to only include fields collected by the playbook */
  filteredData: Partial<T>;
  /** Keys of the filtered bootstrap data (for X-Fp-Bootstrapped-Fields header) */
  bootstrapFields: string[];
};

/**
 * Filters bootstrap data to only include fields that are collected by the playbook.
 * Logs a warning for any bootstrap fields that were provided but not collected.
 *
 * @param bootstrapData - The bootstrap data to filter (may be undefined)
 * @param allowedFields - Array of field keys that are collected by the playbook
 * @returns Filtered bootstrap data and the list of bootstrap field keys
 */
export const filterBootstrapData = <T extends Record<string, unknown>>(
  bootstrapData: T | undefined,
  allowedFields: string[],
): FilterBootstrapDataResult<T> => {
  if (!bootstrapData) {
    return { filteredData: {}, bootstrapFields: [] };
  }

  const allowedFieldsSet = new Set<string>(allowedFields);
  const allBootstrapKeys = Object.keys(bootstrapData);

  const filteredData = Object.fromEntries(
    Object.entries(bootstrapData).filter(([key]) => allowedFieldsSet.has(key)),
  ) as Partial<T>;

  // Normalize phone numbers to E.164 — integrators may pass national formats
  // (e.g. "4155551234") that the backend rejects without a "+" prefix.
  const data = filteredData as Record<string, unknown>;
  const phoneToCountry = [
    ['id.phone_number', 'id.country'],
    ['business.phone_number', 'business.country'],
  ];
  for (const [phoneKey, countryKey] of phoneToCountry) {
    if (typeof data[phoneKey] === 'string') {
      const country = typeof data[countryKey] === 'string' ? (data[countryKey] as CountryCode) : undefined;
      data[phoneKey] = normalizePhoneToE164(data[phoneKey] as string, country);
    }
  }

  const bootstrapFields = Object.keys(filteredData);

  // Log warning for bootstrap fields that were provided but not collected by the playbook
  const ignoredBootstrapFields = allBootstrapKeys.filter(key => !allowedFieldsSet.has(key));
  if (ignoredBootstrapFields.length > 0) {
    logWarn('Bootstrap fields provided but not collected by playbook', undefined, {
      ignoredFields: ignoredBootstrapFields.join(','),
    });
  }

  return { filteredData, bootstrapFields };
};
